import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Generate or retrieve session ID
const getSessionId = () => {
  let sessionId = localStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    localStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Page title mapping
const getPageTitle = (pathname) => {
  const titles = {
    '/': 'Home',
    '/about': 'About Us',
    '/programs': 'Programs',
    '/coffee': 'Safe House Coffee',
    '/donate': 'Donate',
    '/stories': 'Stories',
    '/get-involved': 'Get Involved',
    '/heros': 'The Hero',
    '/admin': 'Admin Panel'
  };
  return titles[pathname] || pathname;
};

const AnalyticsTracker = () => {
  const location = useLocation();
  const pageEntryTime = useRef(Date.now());
  const currentPath = useRef(location.pathname);
  const sessionId = useRef(getSessionId());
  const updateInterval = useRef(null);

  // Record page view
  const recordPageView = useCallback(async (path, timeOnPage = 0) => {
    // Don't track admin pages
    if (path.startsWith('/admin')) return;
    
    try {
      await axios.post(`${API}/api/analytics/pageview`, {
        session_id: sessionId.current,
        page_path: path,
        page_title: getPageTitle(path),
        time_on_page: Math.round(timeOnPage / 1000), // Convert to seconds
        referrer: document.referrer || null,
        screen_width: window.screen?.width || null,
        screen_height: window.screen?.height || null,
        language: navigator.language || null
      });
    } catch (error) {
      // Silently fail - don't disrupt user experience
      console.debug('Analytics error:', error);
    }
  }, []);

  // Update time on page
  const updateTimeOnPage = useCallback(async () => {
    const path = currentPath.current;
    
    // Don't track admin pages
    if (path.startsWith('/admin')) return;
    
    const timeSpent = Date.now() - pageEntryTime.current;
    
    try {
      await axios.post(`${API}/api/analytics/update-time`, {
        session_id: sessionId.current,
        page_path: path,
        time_on_page: Math.round(timeSpent / 1000) // Convert to seconds
      });
      
      // Reset the entry time after updating
      pageEntryTime.current = Date.now();
    } catch (error) {
      console.debug('Analytics update error:', error);
    }
  }, []);

  // Track page changes
  useEffect(() => {
    const previousPath = currentPath.current;
    const previousEntryTime = pageEntryTime.current;
    
    // Calculate time spent on previous page
    const timeOnPreviousPage = Date.now() - previousEntryTime;
    
    // Record the page view for the new page
    if (location.pathname !== previousPath) {
      // Record time spent on previous page (final update)
      if (previousPath && !previousPath.startsWith('/admin')) {
        updateTimeOnPage();
      }
    }
    
    // Record new page view
    recordPageView(location.pathname, 0);
    
    // Update current path and reset entry time
    currentPath.current = location.pathname;
    pageEntryTime.current = Date.now();
    
    // Set up periodic time updates (every 30 seconds)
    if (updateInterval.current) {
      clearInterval(updateInterval.current);
    }
    
    updateInterval.current = setInterval(() => {
      updateTimeOnPage();
    }, 30000); // Update every 30 seconds
    
    // Cleanup on unmount or path change
    return () => {
      if (updateInterval.current) {
        clearInterval(updateInterval.current);
      }
    };
  }, [location.pathname, recordPageView, updateTimeOnPage]);

  // Track when user leaves the page
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        updateTimeOnPage();
      }
    };

    const handleBeforeUnload = () => {
      updateTimeOnPage();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [updateTimeOnPage]);

  // This component doesn't render anything
  return null;
};

export default AnalyticsTracker;
